import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api } from "../api";
import ScreenplayLocationsWorld from "./ScreenplayLocationsWorld";
import ScreenplayContinuityLedger from "./ScreenplayContinuityLedger";

const EMPTY_FORM = {
  name: "",
  role: "",
  physicalDescription: "",
  personality: "",
  values: "",
  weaknesses: "",
  speechStyle: "",
  relationships: "",
  backstory: "",
  goals: "",
  fears: "",
  characterArc: "",
  importantRules: "",
  notes: "",
};

const CHARACTER_LIST_PAGE_SIZE =
  8;

const LONG_FIELDS = [
  "physicalDescription",
  "personality",
  "values",
  "weaknesses",
  "speechStyle",
  "relationships",
  "backstory",
  "goals",
  "fears",
  "characterArc",
  "importantRules",
  "notes",
];

function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function fieldLabel(
  key,
  isFR
) {
  const en = {
    name:
      "Name",
    role:
      "Role",
    physicalDescription:
      "Physical description",
    personality:
      "Personality",
    values:
      "Values",
    weaknesses:
      "Weaknesses",
    speechStyle:
      "Speech style",
    relationships:
      "Relationships",
    backstory:
      "Backstory",
    goals:
      "Goals",
    fears:
      "Fears",
    characterArc:
      "Character arc",
    importantRules:
      "Important rules",
    notes:
      "Notes",
  };

  const fr = {
    name:
      "Nom",
    role:
      "Rôle",
    physicalDescription:
      "Description physique",
    personality:
      "Personnalité",
    values:
      "Valeurs",
    weaknesses:
      "Faiblesses",
    speechStyle:
      "Style de parole",
    relationships:
      "Relations",
    backstory:
      "Passé du personnage",
    goals:
      "Objectifs",
    fears:
      "Peurs",
    characterArc:
      "Évolution du personnage",
    importantRules:
      "Règles importantes",
    notes:
      "Notes",
  };

  return isFR
    ? fr[key] || key
    : en[key] || key;
}

function fieldHelp(
  key,
  isFR
) {
  const en = {
    physicalDescription:
      "Appearance, age, costume cues, posture and visual details worth preserving.",
    personality:
      "Temperament, habits, virtues, flaws and normal behaviour.",
    values:
      "What the character believes in and refuses to betray.",
    weaknesses:
      "Where the character struggles, hesitates or can be tempted into error.",
    speechStyle:
      "How the character speaks: formal, direct, playful, quiet, clipped, poetic, etc.",
    relationships:
      "Family, allies, rivals, mentors and emotional bonds.",
    backstory:
      "Past events that materially shape the character.",
    goals:
      "What the character wants in this screenplay.",
    fears:
      "What the character avoids, fears or must overcome.",
    characterArc:
      "How the character changes, learns, hardens or fails to change.",
    importantRules:
      "Continuity rules collaborators and later AI assistance must not contradict casually.",
    notes:
      "Other useful character facts.",
  };

  const fr = {
    physicalDescription:
      "Apparence, âge, costumes, posture et détails visuels à préserver.",
    personality:
      "Tempérament, habitudes, qualités, défauts et comportement habituel.",
    values:
      "Ce que le personnage croit et refuse de trahir.",
    weaknesses:
      "Ses faiblesses, hésitations ou tentations.",
    speechStyle:
      "Sa manière de parler : formelle, directe, joyeuse, calme, brève, poétique, etc.",
    relationships:
      "Famille, alliés, rivaux, mentors et liens émotionnels.",
    backstory:
      "Événements passés qui façonnent réellement le personnage.",
    goals:
      "Ce que le personnage cherche dans ce scénario.",
    fears:
      "Ce qu’il évite, craint ou doit surmonter.",
    characterArc:
      "Comment le personnage change, apprend, se durcit ou refuse de changer.",
    importantRules:
      "Règles de continuité que les collaborateurs et la future assistance IA ne doivent pas contredire à la légère.",
    notes:
      "Autres faits utiles sur le personnage.",
  };

  return isFR
    ? fr[key] || ""
    : en[key] || "";
}

function characterSearchText(
  character
) {
  return [
    character?.name,
    character?.role,
    character?.physicalDescription,
    character?.personality,
    character?.values,
    character?.weaknesses,
    character?.speechStyle,
    character?.relationships,
    character?.backstory,
    character?.goals,
    character?.fears,
    character?.characterArc,
    character?.importantRules,
    character?.notes,
    character?.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function CompactField({
  label,
  help,
  children,
}) {
  return (
    <label
      style={{
        display:
          "grid",
        gap:
          6,
      }}
    >
      <span
        style={{
          fontWeight:
            700,
          color:
            "#334155",
        }}
      >
        {label}
      </span>

      {help ? (
        <span
          className="ns-card-meta"
          style={{
            margin:
              0,
            lineHeight:
              1.35,
          }}
        >
          {help}
        </span>
      ) : null}

      {children}
    </label>
  );
}

function CharacterCard({
  character,
  isFR,
  canManage,
  onEdit,
  onArchive,
  onRestore,
}) {
  const archived =
    clean(
      character?.status
    ).toLowerCase() ===
    "archived";

  return (
    <article
      className={`screenplay-character-record${
        archived
          ? " screenplay-character-record--archived"
          : ""
      }`}
      style={{
        marginBottom:
          10,
        padding:
          "14px 16px",
        border:
          archived
            ? "1px dashed #cbd5e1"
            : "1px solid #dbe7df",
        borderLeft:
          archived
            ? "4px solid #94a3b8"
            : "4px solid #15803d",
        borderRadius:
          12,
        background:
          archived
            ? "#f8fafc"
            : "#ffffff",
        boxShadow:
          archived
            ? "none"
            : "0 1px 2px rgba(15,23,42,0.04)",
        opacity:
          archived
            ? 0.82
            : 1,
      }}
    >
      <div
        style={{
          display:
            "flex",
          gap:
            12,
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          flexWrap:
            "wrap",
        }}
      >
        <div
          style={{
            minWidth:
              0,
          }}
        >
          <h4
            style={{
              margin:
                "0 0 4px",
              color:
                "#0f172a",
            }}
          >
            {character?.name ||
              (
                isFR
                  ? "Personnage sans nom"
                  : "Unnamed character"
              )}
          </h4>

          <p
            className="ns-card-meta"
            style={{
              margin:
                0,
            }}
          >
            {character?.role ||
              (
                isFR
                  ? "Rôle non défini"
                  : "Role not defined"
              )}

            {archived
              ? ` · ${
                  isFR
                    ? "Archivé"
                    : "Archived"
                }`
              : ""}
          </p>
        </div>

        {canManage ? (
          <div
            style={{
              display:
                "flex",
              gap:
                8,
              flexWrap:
                "wrap",
              justifyContent:
                "flex-end",
            }}
          >
            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={() =>
                onEdit(
                  character
                )
              }
            >
              {isFR
                ? "Modifier"
                : "Edit"}
            </button>

            {archived ? (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  onRestore(
                    character
                  )
                }
              >
                {isFR
                  ? "Restaurer"
                  : "Restore"}
              </button>
            ) : (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  onArchive(
                    character
                  )
                }
              >
                {isFR
                  ? "Archiver"
                  : "Archive"}
              </button>
            )}
          </div>
        ) : null}
      </div>

      {character?.personality ? (
        <p
          style={{
            margin:
              "10px 0 0",
            color:
              "#334155",
            lineHeight:
              1.5,
          }}
        >
          <strong>
            {isFR
              ? "Personnalité"
              : "Personality"}
            :
          </strong>{" "}
          {character.personality}
        </p>
      ) : null}

      {character?.importantRules ? (
        <p
          style={{
            margin:
              "8px 0 0",
            color:
              "#334155",
            lineHeight:
              1.5,
          }}
        >
          <strong>
            {isFR
              ? "Règles importantes"
              : "Important rules"}
            :
          </strong>{" "}
          {
            character
              .importantRules
          }
        </p>
      ) : null}
    </article>
  );
}

export default function ScreenplayCharactersWorld({
  projectId,
  canManage = false,
  active = false,
  isFR = false,
}) {
  const id =
    clean(projectId);

  const formRef =
    useRef(null);

  const archiveCancelRef =
    useRef(null);

  const [
    characters,
    setCharacters,
  ] = useState([]);

  const [
    includeArchived,
    setIncludeArchived,
  ] = useState(false);

  const [
    searchText,
    setSearchText,
  ] = useState("");

  const [
    visibleLimit,
    setVisibleLimit,
  ] = useState(
    CHARACTER_LIST_PAGE_SIZE
  );

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    editing,
    setEditing,
  ] = useState(null);

  const [
    pendingArchive,
    setPendingArchive,
  ] = useState(null);

  const [
    form,
    setForm,
  ] = useState(
    EMPTY_FORM
  );

  const activeCharacters =
    useMemo(
      () =>
        characters.filter(
          (character) =>
            clean(
              character
                ?.status
            ).toLowerCase() !==
            "archived"
        ),
      [characters]
    );

  const archivedCharacters =
    useMemo(
      () =>
        characters.filter(
          (character) =>
            clean(
              character
                ?.status
            ).toLowerCase() ===
            "archived"
        ),
      [characters]
    );

  const visibleCharacters =
    useMemo(() => {
      const query =
        clean(
          searchText
        ).toLowerCase();

      return characters.filter(
        (character) => {
          const archived =
            clean(
              character
                ?.status
            ).toLowerCase() ===
            "archived";

          if (
            archived &&
            !includeArchived
          ) {
            return false;
          }

          if (!query) {
            return true;
          }

          return characterSearchText(
            character
          ).includes(query);
        }
      );
    }, [
      characters,
      includeArchived,
      searchText,
    ]);

  const listedCharacters =
    useMemo(
      () =>
        visibleCharacters.slice(
          0,
          visibleLimit
        ),
      [
        visibleCharacters,
        visibleLimit,
      ]
    );

  const hiddenCount =
    Math.max(
      0,
      visibleCharacters.length -
        listedCharacters.length
    );

  useEffect(() => {
    setVisibleLimit(
      CHARACTER_LIST_PAGE_SIZE
    );
  }, [
    searchText,
    includeArchived,
    id,
  ]);

  useEffect(() => {
    if (!pendingArchive) {
      return undefined;
    }

    const focusTimer =
      window.setTimeout(() => {
        archiveCancelRef.current
          ?.focus();
      }, 0);

    function handleArchiveDialogKeyDown(
      event
    ) {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        setPendingArchive(null);
      }
    }

    document.addEventListener(
      "keydown",
      handleArchiveDialogKeyDown
    );

    return () => {
      window.clearTimeout(
        focusTimer
      );
      document.removeEventListener(
        "keydown",
        handleArchiveDialogKeyDown
      );
    };
  }, [
    pendingArchive,
    saving,
  ]);

  async function loadCharacters() {
    if (!id) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload =
        await api.get(
          `/api/projects/${encodeURIComponent(
            id
          )}/characters?includeArchived=true`
        );

      setCharacters(
        Array.isArray(
          payload?.characters
        )
          ? payload.characters
          : []
      );
    } catch (loadError) {
      setError(
        clean(
          loadError?.message
        ) ||
          (
            isFR
              ? "Impossible de charger les personnages."
              : "Could not load characters."
          )
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (
      !active ||
      !id
    ) {
      return;
    }

    loadCharacters();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    id,
  ]);

  function updateField(
    key,
    value
  ) {
    setForm(
      (current) => ({
        ...current,
        [key]:
          value,
      })
    );
  }

  function resetForm({
    preserveMessage = false,
  } = {}) {
    setEditing(null);
    setForm(
      EMPTY_FORM
    );
    setError("");

    if (
      !preserveMessage
    ) {
      setMessage("");
    }
  }

  function startEdit(
    character
  ) {
    setEditing(
      character
    );

    setForm({
      name:
        character?.name || "",
      role:
        character?.role || "",
      physicalDescription:
        character
          ?.physicalDescription ||
        "",
      personality:
        character
          ?.personality || "",
      values:
        character?.values || "",
      weaknesses:
        character
          ?.weaknesses || "",
      speechStyle:
        character
          ?.speechStyle || "",
      relationships:
        character
          ?.relationships ||
        "",
      backstory:
        character
          ?.backstory || "",
      goals:
        character?.goals || "",
      fears:
        character?.fears || "",
      characterArc:
        character
          ?.characterArc || "",
      importantRules:
        character
          ?.importantRules ||
        "",
      notes:
        character?.notes || "",
    });

    setError("");
    setMessage("");

    setTimeout(() => {
      if (
        formRef.current &&
        typeof formRef.current
          .scrollIntoView ===
          "function"
      ) {
        formRef.current
          .scrollIntoView({
            behavior:
              "smooth",
            block:
              "start",
          });
      }
    }, 50);
  }

  async function saveCharacter(
    event
  ) {
    event.preventDefault();

    if (!id) {
      setError(
        isFR
          ? "Aucun projet de scénario actif."
          : "No active Screenplay project."
      );
      return;
    }

    if (
      !clean(form.name)
    ) {
      setError(
        isFR
          ? "Le nom du personnage est obligatoire."
          : "Character name is required."
      );
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (
        editing?.id ||
        editing?._id
      ) {
        const characterId =
          clean(
            editing.id ||
            editing._id
          );

        await api.put(
          `/api/projects/${encodeURIComponent(
            id
          )}/characters/${encodeURIComponent(
            characterId
          )}`,
          form
        );

        resetForm({
          preserveMessage:
            true,
        });

        setMessage(
          isFR
            ? "Personnage mis à jour."
            : "Character updated."
        );
      } else {
        await api.post(
          `/api/projects/${encodeURIComponent(
            id
          )}/characters`,
          form
        );

        resetForm({
          preserveMessage:
            true,
        });

        setMessage(
          isFR
            ? "Personnage ajouté."
            : "Character added."
        );
      }

      await loadCharacters();
    } catch (saveError) {
      setError(
        clean(
          saveError?.message
        ) ||
          (
            isFR
              ? "Impossible d’enregistrer le personnage."
              : "Could not save character."
          )
      );
    } finally {
      setSaving(false);
    }
  }

  function archiveCharacter(
    character
  ) {
    const characterId =
      clean(
        character?.id ||
        character?._id
      );

    if (
      !id ||
      !characterId
    ) {
      return;
    }

    setError("");
    setMessage("");
    setPendingArchive(
      character
    );
  }

  async function confirmArchiveCharacter() {
    const characterId =
      clean(
        pendingArchive?.id ||
        pendingArchive?._id
      );

    if (
      !id ||
      !characterId ||
      saving
    ) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await api.patch(
        `/api/projects/${encodeURIComponent(
          id
        )}/characters/${encodeURIComponent(
          characterId
        )}/archive`,
        {}
      );

      setPendingArchive(null);

      setMessage(
        isFR
          ? "Personnage archivé."
          : "Character archived."
      );

      await loadCharacters();
    } catch (archiveError) {
      setError(
        clean(
          archiveError?.message
        ) ||
          (
            isFR
              ? "Impossible d’archiver le personnage."
              : "Could not archive character."
          )
      );
    } finally {
      setSaving(false);
    }
  }

  async function restoreCharacter(
    character
  ) {
    const characterId =
      clean(
        character?.id ||
        character?._id
      );

    if (
      !id ||
      !characterId
    ) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await api.patch(
        `/api/projects/${encodeURIComponent(
          id
        )}/characters/${encodeURIComponent(
          characterId
        )}/restore`,
        {}
      );

      setMessage(
        isFR
          ? "Personnage restauré."
          : "Character restored."
      );

      await loadCharacters();
    } catch (restoreError) {
      setError(
        clean(
          restoreError?.message
        ) ||
          (
            isFR
              ? "Impossible de restaurer le personnage."
              : "Could not restore character."
          )
      );
    } finally {
      setSaving(false);
    }
  }

  if (!id) {
    return (
      <section
        className="ns-card"
      >
        <p
          className="ns-card-meta"
          style={{
            margin:
              0,
          }}
        >
          {isFR
            ? "Ouvrez d’abord un projet de scénario."
            : "Open a Screenplay project first."}
        </p>
      </section>
    );
  }

  return (
    <div>
      {pendingArchive ? (
        <div
          className="screenplay-character-archive-backdrop"
          style={{
            position:
              "fixed",
            inset:
              0,
            zIndex:
              10000,
            display:
              "grid",
            placeItems:
              "center",
            padding:
              20,
            background:
              "rgba(15,23,42,0.58)",
          }}
        >
          <section
            className="screenplay-character-archive-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="screenplay-character-archive-title"
            aria-describedby="screenplay-character-archive-description"
            style={{
              width:
                "min(100%, 520px)",
              padding:
                24,
              border:
                "1px solid #e2e8f0",
              borderRadius:
                18,
              background:
                "#ffffff",
              boxShadow:
                "0 24px 70px rgba(15,23,42,0.28)",
            }}
          >
            <div
              style={{
                fontSize:
                  12,
                fontWeight:
                  900,
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
                color:
                  "#b45309",
              }}
            >
              {isFR
                ? "ARCHIVER LE PERSONNAGE"
                : "ARCHIVE CHARACTER"}
            </div>

            <h3
              id="screenplay-character-archive-title"
              style={{
                margin:
                  "7px 0 8px",
                color:
                  "#0f172a",
              }}
            >
              {isFR
                ? `Archiver « ${
                    pendingArchive?.name ||
                    "ce personnage"
                  } » ?`
                : `Archive ${
                    pendingArchive?.name ||
                    "this character"
                  }?`}
            </h3>

            <p
              id="screenplay-character-archive-description"
              className="ns-card-meta"
              style={{
                margin:
                  0,
                lineHeight:
                  1.55,
              }}
            >
              {isFR
                ? "Le personnage quittera la liste active. Il ne sera pas supprimé définitivement et pourra être restauré plus tard."
                : "This character will move out of the active list. It will not be permanently deleted and can be restored later."}
            </p>

            <div
              style={{
                display:
                  "flex",
                flexWrap:
                  "wrap",
                justifyContent:
                  "flex-end",
                gap:
                  10,
                marginTop:
                  22,
              }}
            >
              <button
                ref={archiveCancelRef}
                type="button"
                className="ns-button ns-button-outline"
                disabled={saving}
                onClick={() =>
                  setPendingArchive(null)
                }
              >
                {isFR
                  ? "Annuler"
                  : "Cancel"}
              </button>

              <button
                type="button"
                className="ns-button"
                disabled={saving}
                onClick={
                  confirmArchiveCharacter
                }
                style={{
                  background:
                    "#b45309",
                  borderColor:
                    "#b45309",
                  color:
                    "#ffffff",
                }}
              >
                {saving
                  ? (
                      isFR
                        ? "Archivage…"
                        : "Archiving…"
                    )
                  : (
                      isFR
                        ? "Archiver le personnage"
                        : "Archive character"
                    )}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <section
        className="ns-card"
        style={{
          padding:
            20,
          borderRadius:
            20,
          border:
            "1.5px solid rgba(220,38,38,0.20)",
          marginBottom:
            16,
        }}
      >
        <div
          style={{
            fontSize:
              12,
            fontWeight:
              900,
            letterSpacing:
              "0.1em",
            textTransform:
              "uppercase",
            color:
              "#dc2626",
          }}
        >
          {isFR
            ? "PERSONNAGES ET UNIVERS"
            : "CHARACTERS & WORLD"}
        </div>

        <h3
          className="ns-card-title"
          style={{
            margin:
              "5px 0 6px",
          }}
        >
          {isFR
            ? "Bible des personnages"
            : "Character Bible"}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin:
              0,
            lineHeight:
              1.55,
            maxWidth:
              820,
          }}
        >
          {isFR
            ? "Conservez les personnages, leurs relations, leurs motivations, leur voix et les règles importantes qui protègent la continuité du scénario."
            : "Keep the people of the screenplay consistent: relationships, motivations, voice, history and important continuity rules."}
        </p>

        <div
          className="screenplay-character-ai-note"
          style={{
            marginTop:
              14,
            padding:
              "12px 14px",
            borderLeft:
              "4px solid #94a3b8",
            borderRadius:
              10,
            background:
              "#f8fafc",
          }}
          role="note"
        >
          <strong>
            {isFR
              ? "Assistance IA, plus tard dans ce flux"
              : "AI assistance, later in this flow"}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "5px 0 0",
              lineHeight:
                1.5,
            }}
          >
            {isFR
              ? "Ces fiches sont conçues pour pouvoir guider plus tard des propositions IA fondées sur un personnage. L’IA restera consultative : elle ne modifiera ni la Bible des personnages ni le Canon."
              : "These records are designed to later ground character-led AI proposals. AI will remain advisory: it will not change the Character Bible or Canon."}
          </p>
        </div>
      </section>

      <section
        className="ns-card"
        style={{
          padding:
            20,
          borderRadius:
            20,
        }}
      >
        <div
          style={{
            display:
              "flex",
            flexWrap:
              "wrap",
            gap:
              12,
            alignItems:
              "end",
            justifyContent:
              "space-between",
          }}
        >
          <div
            style={{
              flex:
                "1 1 280px",
            }}
          >
            <label
              style={{
                display:
                  "grid",
                gap:
                  6,
              }}
            >
              <span
                style={{
                  fontWeight:
                    700,
                  color:
                    "#334155",
                }}
              >
                {isFR
                  ? "Rechercher un personnage"
                  : "Find a character"}
              </span>

              <input
                className="ns-input"
                value={
                  searchText
                }
                onChange={(
                  event
                ) =>
                  setSearchText(
                    event
                      .target
                      .value
                  )
                }
                placeholder={
                  isFR
                    ? "Nom, rôle, personnalité, règle…"
                    : "Name, role, personality, rule…"
                }
              />
            </label>
          </div>

          <label
            className="ns-card-meta"
            style={{
              display:
                "inline-flex",
              gap:
                8,
              alignItems:
                "center",
              margin:
                0,
            }}
          >
            <input
              type="checkbox"
              checked={
                includeArchived
              }
              onChange={(
                event
              ) =>
                setIncludeArchived(
                  event
                    .target
                    .checked
                )
              }
            />

            {isFR
              ? "Afficher les archivés"
              : "Show archived"}
          </label>
        </div>

        <p
          className="ns-card-meta"
          style={{
            margin:
              "10px 0 0",
          }}
        >
          {loading
            ? (
                isFR
                  ? "Chargement…"
                  : "Loading…"
              )
            : `${
                activeCharacters
                  .length
              } ${
                isFR
                  ? "actif(s)"
                  : "active"
              } · ${
                archivedCharacters
                  .length
              } ${
                isFR
                  ? "archivé(s)"
                  : "archived"
              } · ${
                visibleCharacters
                  .length
              } ${
                isFR
                  ? "affiché(s)"
                  : "shown"
              }`}
        </p>

        {error ? (
          <div
            className="ns-card ns-card-soft"
            style={{
              marginTop:
                12,
              borderColor:
                "#fecaca",
              color:
                "#991b1b",
            }}
          >
            {error}
          </div>
        ) : null}

        {message ? (
          <div
            className="ns-card ns-card-soft"
            style={{
              marginTop:
                12,
              borderColor:
                "#bbf7d0",
              color:
                "#166534",
            }}
          >
            {message}
          </div>
        ) : null}

        <div
          className="screenplay-character-list"
          style={{
            marginTop:
              18,
            paddingTop:
              16,
            borderTop:
              "1px solid #e2e8f0",
          }}
        >
          <h4
            style={{
              margin:
                "0 0 12px",
              color:
                "#0f172a",
              fontSize:
                18,
            }}
          >
            {isFR
              ? "Personnages"
              : "Characters"}
          </h4>

          {visibleCharacters.length ===
            0 &&
          !loading ? (
            <p
              className="ns-card-meta"
            >
              {characters.length ===
              0
                ? (
                    isFR
                      ? "Aucun personnage n’a encore été ajouté."
                      : "No characters have been added yet."
                  )
                : (
                    isFR
                      ? "Aucun personnage ne correspond à cette recherche."
                      : "No character matches this search."
                  )}
            </p>
          ) : (
            <>
              {listedCharacters.map(
                (
                  character
                ) => (
                  <CharacterCard
                    key={
                      character.id ||
                      character._id ||
                      character.name
                    }
                    character={
                      character
                    }
                    isFR={
                      isFR
                    }
                    canManage={
                      canManage
                    }
                    onEdit={
                      startEdit
                    }
                    onArchive={
                      archiveCharacter
                    }
                    onRestore={
                      restoreCharacter
                    }
                  />
                )
              )}

              {hiddenCount >
              0 ? (
                <div
                  style={{
                    display:
                      "flex",
                    justifyContent:
                      "center",
                    marginTop:
                      10,
                  }}
                >
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() =>
                      setVisibleLimit(
                        (
                          current
                        ) =>
                          current +
                          CHARACTER_LIST_PAGE_SIZE
                      )
                    }
                  >
                    {isFR
                      ? `Afficher ${Math.min(
                          CHARACTER_LIST_PAGE_SIZE,
                          hiddenCount
                        )} de plus`
                      : `Show ${Math.min(
                          CHARACTER_LIST_PAGE_SIZE,
                          hiddenCount
                        )} more`}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>

        {canManage ? (
          <form
            ref={
              formRef
            }
            onSubmit={
              saveCharacter
            }
            className="screenplay-character-form"
            style={{
              marginTop:
                22,
              padding:
                18,
              borderTop:
                "2px solid #dbe7df",
              borderRadius:
                14,
              background:
                "#f8fafc",
            }}
          >
            <h4
              style={{
                marginTop:
                  0,
              }}
            >
              {editing
                ? (
                    isFR
                      ? `Modifier : ${
                          editing.name ||
                          ""
                        }`
                      : `Edit: ${
                          editing.name ||
                          ""
                        }`
                  )
                : (
                    isFR
                      ? "Ajouter un personnage"
                      : "Add character"
                  )}
            </h4>

            <div
              style={{
                display:
                  "grid",
                gap:
                  14,
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(240px, 1fr))",
              }}
            >
              <CompactField
                label={`${fieldLabel(
                  "name",
                  isFR
                )} *`}
              >
                <input
                  className="ns-input"
                  value={
                    form.name
                  }
                  onChange={(
                    event
                  ) =>
                    updateField(
                      "name",
                      event
                        .target
                        .value
                    )
                  }
                  placeholder={
                    isFR
                      ? "Ex. Personnage principal"
                      : "Example: Main character"
                  }
                />
              </CompactField>

              <CompactField
                label={fieldLabel(
                  "role",
                  isFR
                )}
              >
                <input
                  className="ns-input"
                  value={
                    form.role
                  }
                  onChange={(
                    event
                  ) =>
                    updateField(
                      "role",
                      event
                        .target
                        .value
                    )
                  }
                  placeholder={
                    isFR
                      ? "Ex. Protagoniste"
                      : "Example: Protagonist"
                  }
                />
              </CompactField>
            </div>

            <div
              style={{
                display:
                  "grid",
                gap:
                  16,
                marginTop:
                  16,
              }}
            >
              {LONG_FIELDS.map(
                (key) => (
                  <CompactField
                    key={
                      key
                    }
                    label={fieldLabel(
                      key,
                      isFR
                    )}
                    help={fieldHelp(
                      key,
                      isFR
                    )}
                  >
                    <textarea
                      className="ns-input"
                      rows={
                        key ===
                          "backstory" ||
                        key ===
                          "notes"
                          ? 4
                          : 3
                      }
                      value={
                        form[
                          key
                        ]
                      }
                      onChange={(
                        event
                      ) =>
                        updateField(
                          key,
                          event
                            .target
                            .value
                        )
                      }
                      style={{
                        width:
                          "100%",
                        minHeight:
                          key ===
                          "importantRules"
                            ? 110
                            : 90,
                      }}
                    />
                  </CompactField>
                )
              )}
            </div>

            <div
              style={{
                display:
                  "flex",
                flexWrap:
                  "wrap",
                gap:
                  10,
                marginTop:
                  16,
              }}
            >
              <button
                type="submit"
                className="ns-button"
                disabled={
                  saving
                }
              >
                {saving
                  ? (
                      isFR
                        ? "Enregistrement…"
                        : "Saving…"
                    )
                  : editing
                  ? (
                      isFR
                        ? "Enregistrer les modifications"
                        : "Save changes"
                    )
                  : (
                      isFR
                        ? "Ajouter le personnage"
                        : "Add character"
                    )}
              </button>

              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  resetForm()
                }
                disabled={
                  saving
                }
              >
                {editing
                  ? (
                      isFR
                        ? "Annuler"
                        : "Cancel"
                    )
                  : (
                      isFR
                        ? "Effacer le formulaire"
                        : "Clear form"
                    )}
              </button>
            </div>
          </form>
        ) : (
          <div
            className="ns-card ns-card-soft"
            style={{
              marginTop:
                16,
            }}
          >
            <p
              className="ns-card-meta"
              style={{
                margin:
                  0,
              }}
            >
              {isFR
                ? "Vous pouvez consulter la Bible des personnages. Seul le propriétaire du projet peut modifier ces fiches."
                : "You can read the Character Bible. Only the project Owner can change these records."}
            </p>
          </div>
        )}
      </section>

      <ScreenplayLocationsWorld
        projectId={id}
        canManage={canManage}
        active={active}
        isFR={isFR}
      />

      <ScreenplayContinuityLedger
        projectId={id}
        canManage={canManage}
        active={active}
        isFR={isFR}
      />
    </div>
  );
}
